import { useEffect, useState } from 'react';
import { useKnowledgeStore } from '@/stores/knowledge';
import { resolutionApi, type CanonicalOntologyData, type OntologicalRelationData } from '@/services/api';

// UOS class color map (mirrors ontology.py)
const CLASS_COLORS: Record<string, string> = {
  terminal_value:       '#f59e0b',
  instrumental_value:   '#fb923c',
  functional_need:      '#3b82f6',
  emotional_need:       '#8b5cf6',
  social_need:          '#ec4899',
  purchase_scenario:    '#10b981',
  life_stage_context:   '#06b6d4',
  market_concept:       '#6366f1',
  brand_attribute:      '#a78bfa',
  product_attribute:    '#60a5fa',
  consumer_archetype:   '#f43f5e',
  self_concept_signal:  '#e879f9',
  friction_factor:      '#ef4444',
  decision_trigger:     '#f97316',
  proposed_extension:   '#9ca3af',
  uncategorized:        '#6b7280',
};

// Relation type groupings for display
const RELATION_GROUPS: Record<string, { label: string; color: string }> = {
  is_instance_of:             { label: 'Classification', color: '#f59e0b' },
  specializes:                { label: 'Classification', color: '#f59e0b' },
  broader_concept_is:         { label: 'Classification', color: '#f59e0b' },
  expresses_value:            { label: 'Value Grounding', color: '#10b981' },
  realized_by:                { label: 'Value Grounding', color: '#10b981' },
  satisfies_need:             { label: 'Need Connection', color: '#8b5cf6' },
  creates_need_for:           { label: 'Need Connection', color: '#8b5cf6' },
  activated_by:               { label: 'Context', color: '#06b6d4' },
  activates_salience_of:      { label: 'Context', color: '#06b6d4' },
  has_defining_attribute:     { label: 'Structure', color: '#6366f1' },
  characterized_by_archetype: { label: 'Structure', color: '#6366f1' },
  in_value_tension_with:      { label: 'Tension', color: '#ef4444' },
  presupposes:                { label: 'Dependency', color: '#94a3b8' },
  co_constitutes:             { label: 'Dependency', color: '#94a3b8' },
  proposes_extension_of:      { label: 'Schema Proposal', color: '#9ca3af' },
};

function relGroup(relType: string) {
  return RELATION_GROUPS[relType] ?? { label: relType, color: '#94a3b8' };
}

function fmtRelType(relType: string) {
  return relType.replace(/_/g, ' ');
}

function ClassBadge({ cls, confidence }: { cls: string; confidence?: number }) {
  const color = CLASS_COLORS[cls] ?? '#94a3b8';
  const label = cls.replace(/_/g, ' ');
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-semibold text-white"
      style={{ backgroundColor: color }}
    >
      {label}
      {confidence !== undefined && confidence > 0 && (
        <span className="opacity-80 text-[10px]">{(confidence * 100).toFixed(0)}%</span>
      )}
    </span>
  );
}

function RelationRow({ rel }: { rel: OntologicalRelationData }) {
  const group = relGroup(rel.relation_type);
  return (
    <div className="bg-white rounded-lg border border-slate-200 p-3 space-y-1.5">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <span
            className="px-1.5 py-0.5 rounded text-[10px] font-bold text-white shrink-0"
            style={{ backgroundColor: group.color }}
          >
            {group.label}
          </span>
          <span className="text-xs font-mono text-slate-600 shrink-0">
            {fmtRelType(rel.relation_type)}
          </span>
          <span className="text-slate-400 text-xs">→</span>
          <span className="text-sm text-slate-800 font-medium">
            {rel.target_label || rel.target_id}
          </span>
          {rel.target_type === 'uos_class' && (
            <span className="text-[10px] text-slate-500 border border-slate-300 px-1 rounded">
              UOS
            </span>
          )}
        </div>
        <span className="text-xs text-slate-400 shrink-0">
          {(rel.confidence * 100).toFixed(0)}%
        </span>
      </div>
      {rel.rationale && (
        <p className="text-xs text-slate-500 leading-relaxed italic">{rel.rationale}</p>
      )}
      {rel.proposed_class_name && (
        <div className="mt-1 px-2 py-1 bg-amber-50 border border-amber-200 rounded text-xs text-amber-700">
          Proposes new UOS class: <strong>{rel.proposed_class_name}</strong>
          {!rel.human_reviewed && (
            <span className="ml-2 text-amber-500">· pending review</span>
          )}
        </div>
      )}
    </div>
  );
}

export function OntologyPanel() {
  const { selectedCanonical } = useKnowledgeStore();
  const [data, setData] = useState<CanonicalOntologyData | null>(null);
  const [loading, setLoading] = useState(false);
  const [classifying, setClassifying] = useState(false);

  useEffect(() => {
    if (!selectedCanonical) { setData(null); return; }
    setLoading(true);
    resolutionApi.getCanonicalOntology(selectedCanonical.id)
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [selectedCanonical?.id]);

  const handleClassify = async () => {
    if (!selectedCanonical) return;
    setClassifying(true);
    try {
      await resolutionApi.classifyCanonical(selectedCanonical.id);
      // Poll for result after a short delay
      setTimeout(async () => {
        try {
          const result = await resolutionApi.getCanonicalOntology(selectedCanonical.id);
          setData(result);
        } finally {
          setClassifying(false);
        }
      }, 8000);
    } catch {
      setClassifying(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32 text-slate-400">
        Loading ontological classification...
      </div>
    );
  }

  if (!selectedCanonical) {
    return (
      <div className="flex items-center justify-center h-32 text-slate-400">
        Select a canonical entity
      </div>
    );
  }

  const hasClassification = !!data?.ontological_class && data.ontological_class !== 'uncategorized';
  const relations = data?.definitional_relations ?? [];

  // Group relations by group label
  const grouped: Record<string, OntologicalRelationData[]> = {};
  for (const rel of relations) {
    const g = relGroup(rel.relation_type).label;
    if (!grouped[g]) grouped[g] = [];
    grouped[g].push(rel);
  }

  return (
    <div className="space-y-4">
      {/* Classification Header */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-slate-700">Ontological Classification</h3>
          <button
            onClick={handleClassify}
            disabled={classifying}
            className="text-xs px-3 py-1.5 rounded-lg bg-brand-teal hover:bg-brand-teal-dark disabled:opacity-50 text-white font-medium transition-colors"
          >
            {classifying ? 'Classifying…' : hasClassification ? 'Re-classify' : 'Classify'}
          </button>
        </div>

        {hasClassification ? (
          <div className="space-y-2">
            <div className="flex items-center gap-2 flex-wrap">
              <ClassBadge
                cls={data!.ontological_class!}
                confidence={data!.ontological_classification_confidence}
              />
              {data?.proposed_new_class && (
                <span className="text-xs text-amber-600 border border-amber-200 bg-amber-50 px-2 py-0.5 rounded-full">
                  Proposes: {data.proposed_new_class}
                </span>
              )}
            </div>
            {data?.ontological_class_description && (
              <p className="text-xs text-slate-500">{data.ontological_class_description}</p>
            )}
            {data?.ontological_class_label && (
              <div className="text-xs text-slate-400">
                UOS Class: <span className="text-slate-600">{data.ontological_class_label}</span>
              </div>
            )}
          </div>
        ) : (
          <div className="text-sm text-slate-400">
            {classifying
              ? 'Reasoning in progress — this takes ~10 seconds…'
              : 'Not yet classified. Click Classify to run the ontological reasoning agent.'}
          </div>
        )}
      </div>

      {/* Definitional Axioms */}
      {relations.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-slate-700">
            Definitional Axioms
            <span className="ml-2 text-xs text-slate-400 font-normal">
              ({relations.length} — what this concept fundamentally IS)
            </span>
          </h3>

          {Object.entries(grouped).map(([groupLabel, rels]) => (
            <div key={groupLabel}>
              <div className="text-xs text-slate-400 uppercase tracking-wider mb-1.5 font-medium">
                {groupLabel}
              </div>
              <div className="space-y-2">
                {rels.map((rel) => (
                  <RelationRow key={rel.id} rel={rel} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Empty state after classification */}
      {hasClassification && relations.length === 0 && !classifying && (
        <div className="text-sm text-slate-400 text-center py-4">
          No definitional axioms inferred yet.
        </div>
      )}

      {/* UOS Explanation footer */}
      <div className="text-xs text-slate-400 border-t border-slate-200 pt-3">
        Layer 1 — Ontological Definition. Grounded in Schwartz value theory, Maslow, SDT, BDI.
        Distinct from Layer 2 market dynamics relations (Graph tab).
      </div>
    </div>
  );
}

/**
 * Compact ontological class badge for use in list views.
 */
export function OntologicalClassBadge({
  ontologicalClass,
  confidence,
}: {
  ontologicalClass?: string;
  confidence?: number;
}) {
  if (!ontologicalClass || ontologicalClass === 'uncategorized') return null;
  return (
    <ClassBadge cls={ontologicalClass} confidence={confidence} />
  );
}
